import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { Navigate, useNavigate } from 'react-router-dom'
import { useStateContext } from '../../services/contexts/StateContext'
import { PaystackCheckoutButton } from '../../components/general-components/PaystackCheckoutButton'
import { CartProductComponent } from '../../components/general-components/CartProductComponent'
import { calculateShipping, placeOrder } from '../../services/serverRequest'
import { ValidateContactData, ValidateLocationData } from '../../services/utilities/errorHandler'
import { formatLocationData, formatContactData } from '../../services/utilities/cartUtils'
import ShippingInfoModal from '../../components/general-components/ShippingInfoModal'
import DataService from '../../services/providers/data.service'
import { FaSpinner } from 'react-icons/fa'
import visa from '../../assets/Visa-card-dark.png'
import master from '../../assets/MasterCard-dark.png'

export const CartPage = () => {
  const navigate = useNavigate()
  const { totalPrice, cartItems, currentUser, reset } = useStateContext()

  const [saveInfo, setSaveInfo] = useState(false)
  const [lock, setLock] = useState(false)
  const [shippingLocations, setShippingLocations] = useState([])
  const [openShipping, setOpenShipping] = useState(false)
  const [location, setLocation] = useState(null)
  
  // New UI Loading States
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [order, setOrder] = useState({
    userCart: cartItems,
    subtotal: totalPrice,
    shipping: 0,
    total: totalPrice,
    customerFName: '',
    customerLName: '',
    customerEmail: '',
    customerPhone: '',
    userUID: currentUser ? currentUser.id : "Guest User",
    address: '',
    address_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  })

  // 1. FIXED: shipping cost calculation only triggers if location is valid
  useEffect(() => {
    if (!location || !location.address || !location.city) return;

    const calculate = async () => {
      setIsCalculatingShipping(true)
      try {
        const value = await calculateShipping(location, cartItems)
        // Check if value is an object or raw integer depending on API response
        const shippingCost = typeof value === 'object' ? parseInt(value.cost || 0) : parseInt(value || 0)
        
        setOrder(o => ({
          ...o,
          userCart: cartItems,
          shipping: shippingCost,
          subtotal: totalPrice,
          total: totalPrice + shippingCost,
        }))
      } catch (err) {
        toast.error("Uh-Oh. There was a problem calculating your shipping. Please try again")
      } finally {
        setIsCalculatingShipping(false)
      }
    }

    calculate()
  }, [location, cartItems, totalPrice])

  // 2. Get user info profile defaults
  useEffect(() => {
    const getUser = async () => {
      try {
        const data = await DataService.getDocument("users", currentUser.id)
        const userData = data?.data()
        if (userData) {
          setOrder(o => ({
            ...o,
            customerFName: userData.fname || '',
            customerLName: userData.lname || '',
            customerEmail: userData.email || '',
            customerPhone: userData.phone || '',
            address: userData.defaultAddress?.address || '',
            address_2: userData.defaultAddress?.address_2 || '',
            city: userData.defaultAddress?.city || '',
            state: userData.defaultAddress?.state || '',
            postal_code: userData.defaultAddress?.postal_code || '',
            country: userData.defaultAddress?.country || '',
          }))
        }
      } catch (err) {
        toast.error(err.message)
      }
    }

    if (currentUser?.id) {
      getUser()
    }
  }, [currentUser])

  // 3. Fetch list of available shipping operations locations
  useEffect(() => {
    const getShipping = async () => {
      try {
        const locations = await DataService.getAllShipping()
        setShippingLocations([...locations.docs.map(item => ({ id: item.id, ...item.data() }))])
      } catch (err) {
        toast.error("Failed to load delivery regions.")
      }
    }
    getShipping()
  }, [])

  const inputHandler = (e) => {
    setOrder(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  // 4. FIXED: Clean form preservation state alignment & verification using extracted utils
  const handleSave = async (e) => {
    e.preventDefault()
    setIsSaving(true)

    const savedLocation = formatLocationData(order)
    const savedContact = formatContactData(order)

    let locErrors = ValidateLocationData(savedLocation, shippingLocations)
    let conErrors = ValidateContactData(savedContact)

    if (locErrors?.length > 0 || conErrors?.length > 0) {
      toast.error(`${locErrors || ''} ${conErrors || ''}`.trim())
      setIsSaving(false)
      return
    }

    // Small delay to ensure users visually register that their data was successfully saved
    await new Promise((resolve) => setTimeout(resolve, 600))

    setLocation(savedLocation)
    setSaveInfo(true)
    setIsSaving(false)
  }

  // 5. FIXED: Accurate target checking on `order.shipping` values instead of the location array
  const handleTransfer = async (e) => {
    e.preventDefault()
    if (!saveInfo) {
      toast.error("Please Fill In All Required Information")
      return
    }
    if (order.shipping <= 0) {
      toast.error("Please Wait While Shipping Calculates (or verify location updates)")
      return
    }

    setLock(true)
    const transferButton = document.getElementById("transfer-button")
    if (transferButton) transferButton.classList.add("spinning")

    try {
      const id = await placeOrder(order)
      toast.success("Your Order Has Been Successfully Placed")
      reset()
      navigate(`/order-receipt/${id}`)
    } catch (err) {
      toast.error(err.message || "An unexpected error occurred processing your transfer.")
    } finally {
      setLock(false)
      if (transferButton) transferButton.classList.remove("spinning")
    }
  }

  return (cartItems.length > 0 ? (
    <div className='w-full flex flex-col lg:flex-row lg:justify-center pb-[25vh]'>
      {openShipping && <ShippingInfoModal shipping={shippingLocations} setOpen={setOpenShipping} />}
      
      {/* Customer Forms */}
      <div className='w-full lg:w-[45%] mt-10'>
        <div className='grid grid-cols-2 w-full h-[40rem] lg:h-[45rem]'>
          
          {!saveInfo ? (
            <form onSubmit={handleSave} className='col-span-2 grid grid-cols-4 text-white text-xl md:text-2xl lg:text-3xl h-full'>
              <div className='col-span-4 h-10 bg-gradient-to-b from-gray-600 via-gray-800 to-black text-white flex justify-center items-center text-xl md:text-2xl lg:text-3xl'>Contact Information</div>
              
              <input type='text' placeholder="First Name*" name='customerFName' value={order.customerFName} className='col-span-2 border-2 rounded-lg h-10 text-black pl-2' onChange={inputHandler} required />
              <input type='text' placeholder="Last Name*" name='customerLName' value={order.customerLName} className='col-span-2 w-full border-2 rounded-lg h-10 text-black pl-2' onChange={inputHandler} required />
              <input type='email' placeholder="Email Address*" name='customerEmail' value={order.customerEmail} className='col-span-4 w-full border-2 rounded-lg h-10 pl-5 text-black' onChange={inputHandler} required />
              <input type='number' placeholder="Phone Number*" name='customerPhone' value={order.customerPhone} className='col-span-4 w-full border-2 rounded-lg h-10 pl-5 text-black' onChange={inputHandler} required />

              <div className='col-span-4 h-10 bg-gradient-to-b from-gray-600 via-gray-800 to-black text-white flex justify-center items-center text-xl md:text-2xl lg:text-3xl'>Delivery Information</div>
              
              <input type='text' placeholder="Street Address*" name='address' value={order.address} className='col-span-4 border-2 rounded-lg h-10 text-black pl-2' onChange={inputHandler} required />
              <input type='text' placeholder="Street Address Line 2" name='address_2' value={order.address_2} className='col-span-4 w-full border-2 rounded-lg h-10 text-black pl-2' onChange={inputHandler} />
              
              <select onChange={inputHandler} className='col-span-2 w-full border-2 rounded-lg h-10 text-black' name='city' value={order.city} required>
                <option value="">City/Locality*</option>
                {Array.from(new Set(shippingLocations?.map(item => item.city?.toUpperCase()))).sort().map((city, index) => (
                  <option key={index} value={city}>{city}</option>
                ))}
              </select>

              <select onChange={inputHandler} className='col-span-2 w-full border-2 rounded-lg h-10 text-black' name='state' value={order.state} required>
                <option value="">State/Province*</option>
                {Array.from(new Set(shippingLocations?.map(item => item.state?.toUpperCase()))).sort().map((state, index) => (
                  <option key={index} value={state}>{state}</option>
                ))}
              </select>

              <input type='text' placeholder="Postal Code" name='postal_code' value={order.postal_code} className='col-span-2 w-full border-2 rounded-lg h-10 text-black pl-2' onChange={inputHandler} />

              <select onChange={inputHandler} className='col-span-2 w-full border-2 rounded-lg h-10 text-black' name='country' value={order.country} required>
                <option value="">Country*</option>
                {Array.from(new Set(shippingLocations?.map(item => item.country?.toUpperCase()))).sort().map((country, index) => (
                  <option key={index} value={country}>{country}</option>
                ))}
              </select>

              <button 
                type='submit' 
                disabled={isSaving}
                className="col-span-2 h-[50%] flex justify-center items-center text-xl col-start-2 tonedButton bg-gradient-to-b from-green-400 via-green-600 to-green-800 mt-10 transition-opacity disabled:opacity-70"
              >
                {isSaving ? <FaSpinner className="animate-spin" size={24} color="#ffffff" /> : 'SAVE'}
              </button>
            </form>
          ) : (
            <div className='col-span-2 grid grid-cols-5 text-xl md:text-2xl lg:text-3xl h-full'>
              <div className='col-span-5 h-10 bg-gradient-to-b from-gray-600 via-gray-800 to-black text-white flex justify-center items-center text-lg md:text-2xl lg:text-3xl'>Contact Information</div>
              <div className='col-span-2 h-10 bg-white text-primary flex justify-center items-center text-lg md:text-xl lg:text-2xl'>First Name:</div>
              <div className='col-span-3 h-10 bg-white text-black flex justify-center items-center text-lg md:text-xl lg:text-2xl'>{order.customerFName}</div>
              <div className='col-span-2 h-10 bg-white text-primary flex justify-center items-center text-lg md:text-xl lg:text-2xl'>Last Name:</div>
              <div className='col-span-3 h-10 bg-white text-black flex justify-center items-center text-lg md:text-xl lg:text-2xl'>{order.customerLName}</div>
              <div className='col-span-2 h-10 bg-white text-primary flex justify-center items-center text-lg md:text-xl lg:text-2xl'>Email Address:</div>
              <div className='col-span-3 h-10 bg-white text-black flex justify-center items-center text-lg md:text-xl lg:text-2xl'>{order.customerEmail}</div>
              <div className='col-span-2 h-10 bg-white text-primary flex justify-center items-center text-lg md:text-xl lg:text-2xl'>Phone Number:</div>
              <div className='col-span-3 h-10 bg-white text-black flex justify-center items-center text-lg md:text-xl lg:text-2xl'>{order.customerPhone}</div>

              <div className='col-span-5 h-10 bg-gradient-to-b from-gray-600 via-gray-800 to-black text-white flex justify-center items-center text-lg md:text-2xl lg:text-3xl'>Delivery Information</div>
              <div className='col-span-2 h-10 bg-white text-primary flex justify-center items-center text-lg md:text-xl lg:text-2xl'>Address:</div>
              <div className='col-span-3 h-10 bg-white text-black flex justify-center items-center text-lg md:text-xl lg:text-2xl'>{order.address}</div>
              <div className='col-span-2 h-10 bg-white text-primary flex justify-center items-center text-lg md:text-xl lg:text-2xl'>Address 2:</div>
              <div className='col-span-3 h-10 bg-white text-black flex justify-center items-center text-lg md:text-xl lg:text-2xl'>{order.address_2}</div>
              <div className='col-span-2 h-10 bg-white text-primary flex justify-center items-center text-lg md:text-xl lg:text-2xl'>City:</div>
              <div className='col-span-3 h-10 bg-white text-black flex justify-center items-center text-lg md:text-xl lg:text-2xl'>{order.city}</div>
              <div className='col-span-2 h-10 bg-white text-primary flex justify-center items-center text-lg md:text-xl lg:text-2xl'>State:</div>
              <div className='col-span-3 h-10 bg-white text-black flex justify-center items-center text-lg md:text-xl lg:text-2xl'>{order.state}</div>
              <div className='col-span-2 h-10 bg-white text-primary flex justify-center items-center text-lg md:text-xl lg:text-2xl'>Postal Code:</div>
              <div className='col-span-3 h-10 bg-white text-black flex justify-center items-center text-lg md:text-xl lg:text-2xl'>{order.postal_code}</div>
              <div className='col-span-2 h-10 bg-white text-primary flex justify-center items-center text-lg md:text-xl lg:text-2xl'>Country:</div>
              <div className='col-span-3 h-10 bg-white text-black flex justify-center items-center text-lg md:text-xl lg:text-2xl'>{order.country}</div>

              <button className="col-span-3 h-[50%] text-xl col-start-2 tonedButton bg-gradient-to-b from-red-400 via-red-600 to-red-800 mt-10"
                onClick={() => setSaveInfo(false)} disabled={lock}>
                EDIT
              </button>
            </div>
          )}

          <div className='col-span-2 w-full h-[10rem] flex flex-col items-center justify-center'>
            <h3 className='text-2xl cursor-pointer underline text-secondary underline-offset-4 decoration-secondary hover:text-secondary' onClick={() => setOpenShipping(true)}>Our Shipping Locations</h3>
          </div>
        </div>
      </div>

      {/* Cart Info and Payment Processing */}
      <div className='w-full lg:w-[45%] flex flex-col items-center mt-10 lg:ml-10'>
        <h5 className='text-2xl font-extrabold my-5 bg-gradient-to-b from-gray-600 via-gray-800 to-black text-white w-full text-center py-3'>Order Summary</h5>

        <div className='w-full max-h-[50vh] overflow-y-scroll flex flex-col items-center lg:mb-10'>
          {cartItems.map((item, i) => (<CartProductComponent key={i} item={item} index={i} />))}
        </div>

        <div className='w-full flex justify-between items-center mt-5 text-primary bg-white py-2'>
          <h5 className='text-lg font-extrabold mx-5'>Subtotal:</h5>
          <h5 className='text-lg font-extrabold mx-5'>&#8358;{totalPrice}</h5>
        </div>

        <div className='w-full flex justify-between items-center mt-5 text-primary bg-white py-2'>
          <h5 className='text-lg font-light mx-5'>Shipping:</h5>
          <h5 className='text-lg font-light mx-5 flex items-center justify-end min-w-[60px]'>
            {isCalculatingShipping ? <FaSpinner className="animate-spin" size={18} /> : <span>&#8358;{order.shipping}</span>}
          </h5>
        </div>

        <div className='w-full flex justify-between items-center mt-5 bg-gradient-to-b from-gray-600 via-gray-800 to-black py-3'>
          <h5 className='text-lg font-extrabold mx-5'>Order Total:</h5>
          <h5 className='text-lg font-extrabold mx-5'>&#8358;{order.total}</h5>
        </div>

        <div className='my-10 lg:w-full flex flex-col lg:flex-row justify-between'>
          <PaystackCheckoutButton setLock={setLock} lock={lock} order={order} saveInfo={saveInfo} />
          <button className="w-72 flex justify-center items-center tonedButton bg-gradient-to-b from-green-400 via-green-600 to-green-800 mt-10 lg:mt-0 transition-opacity disabled:opacity-70"
            onClick={handleTransfer} disabled={lock || isCalculatingShipping} id="transfer-button">
            {lock ? <FaSpinner className="animate-spin" size={24} color="#ffffff" /> : 'Pay With Bank Transfer'}
          </button>
        </div>

        <div className='w-full h-[10vh] flex flex-row items-center justify-center'>
          <img src={visa} alt='visa' className='w-[20] h-[12] mr-2' />
          <img src={master} alt='mastercard' className='w-[20] h-[12] ml-2' />
        </div>
      </div>
      
    </div>
  ) : <Navigate to='/shop' />)
}